/**
 * The one control Broadsheet does not draw and `@wetalk/ui` does not wrap: a switch.
 *
 * It is a **native checkbox with `role="switch"`**. The track and the knob are CSS on the
 * real control, so the keyboard, the label association, the focus ring and the checked state
 * all come from the platform. The alternative — a `<div>` with a click handler and
 * `aria-checked` — is what the prototype draws, and it is unreachable by keyboard unless
 * every one of those is re-implemented by hand, per switch.
 *
 * It lives under `billing/` because P11-07 is the unit that needed it first; Settings and the
 * Test console import it from here rather than growing a second copy, because two switches
 * that can drift apart is exactly the failure this file exists to avoid.
 */
import type { ReactNode } from "react";

export interface SwitchControlProps {
  /** The accessible name. Always present: a switch with no name says nothing. */
  readonly label: string;
  /**
   * `false` where the design draws the bare switch beside a title that already names it
   * (Billing, the Channel detail). The name is still on the control, visually hidden.
   */
  readonly labelVisible?: boolean;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onChange: (next: boolean) => void;
}

export function SwitchControl({
  label,
  labelVisible = true,
  checked,
  disabled = false,
  onChange,
}: SwitchControlProps): ReactNode {
  return (
    <label className="c-switch">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.currentTarget.checked);
        }}
      />
      <span className="c-switch-track" aria-hidden="true">
        <span className="c-switch-knob" />
      </span>
      <span className={labelVisible ? undefined : "wt-visually-hidden"}>{label}</span>
    </label>
  );
}
